import React, { useState } from 'react';
import './App.css';

// Core Layout Components
import Header from './components/Layout/Header';
import Sidebar from './components/Layout/Sidebar';

// Page Components
import DashboardPage from './components/pages/DashboardPage';

// Feature Components
import KnowledgeBaseDashboard from './components/KnowledgeBase/KnowledgeBaseDashboard';
import WidgetDashboard from './components/Widget/WidgetDashboard';
import WidgetBuilder from './components/Widget/WidgetBuilder';
import LicensePanel from './components/License/LicensePanel';
import IntegrationsPanel from './components/Integrations/IntegrationsPanel';

// Authentication Components
import LoginForm from './components/Auth/LoginForm';
import RegisterForm from './components/Auth/RegisterForm';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [showRegister, setShowRegister] = useState(false);
  const [userWidgets, setUserWidgets] = useState([]);
  const [showWidgetPreview, setShowWidgetPreview] = useState(false);
  const [licenseKey, setLicenseKey] = useState(null);
  const [isLicenseActive, setIsLicenseActive] = useState(false);
  const [editingWidgetId, setEditingWidgetId] = useState(null);
  const [widgetView, setWidgetView] = useState('list'); // 'list' or 'builder'
 
  const currentUser = {
    id: 'user_123',
    name: 'Developer',
    email: 'admin@company.com',
    plan: 'pro',
    role: 'Admin'
  };

  const mockUsers = [
    {
      id: 1,
      name: 'Admin User',
      email: 'admin@company.com',
      password: 'admin123',
      role: 'Admin'
    }
  ];

  const handleLogin = (credentials) => {
    const user = mockUsers.find(u =>
      u.email === credentials.email && u.password === credentials.password
    );
   
    if (user) {
      setIsLoggedIn(true);
      setShowRegister(false);
    } else {
      alert('Invalid credentials. Try: admin@company.com / admin123');
    }
  };

  const handleRegister = (userData) => {
    setIsLoggedIn(true);
    setShowRegister(false);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setCurrentPage('dashboard');
    setShowRegister(false);
  };

  const handleSaveWidget = (widgetData) => {
    setUserWidgets(prev => [...prev, widgetData]);
    console.log('Widget saved:', widgetData);
  };

  const handleNavigate = (pageId) => {
    setCurrentPage(pageId);
    // Reset widget view when navigating away from widgetBuilder
    if (pageId !== 'widgetBuilder') {
      setWidgetView('list');
      setEditingWidgetId(null);
    }
  };

  const handleActivateLicense = (key) => {
    setLicenseKey(key);
    setIsLicenseActive(!!key);
  };

  // Widget Dashboard handlers
  const handleCreateNewWidget = () => {
    setEditingWidgetId(null);
    setWidgetView('builder');
  };

  const handleEditWidget = (widgetId) => {
    setEditingWidgetId(widgetId);
    setWidgetView('builder');
  };

  const handleBackToWidgetList = () => {
    setEditingWidgetId(null);
    setWidgetView('list');
  };

  const renderMainContent = () => {
    switch (currentPage) {
      case 'dashboard':
        return (
          <DashboardPage
            userWidgets={userWidgets}
            isLicenseActive={isLicenseActive}
            onNavigate={handleNavigate}
          />
        );

      case 'knowledgeBase':
        return (
          <KnowledgeBaseDashboard
            onBackToDashboard={() => setCurrentPage('dashboard')}
            currentUser={currentUser}
          />
        );

      case 'widgetBuilder':
        return (
          <div>
            {widgetView === 'builder' && (
              <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb', background: 'white' }}>
                <button 
                  onClick={handleBackToWidgetList}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  ← Back to Widget List
                </button>
              </div>
            )}
            
            {widgetView === 'list' ? (
              <WidgetDashboard
                currentUser={currentUser}
                onEditWidget={handleEditWidget}
                onCreateNew={handleCreateNewWidget}
              />
            ) : (
              <WidgetBuilder
                currentUser={currentUser}
                onSave={handleSaveWidget}
                editingWidgetId={editingWidgetId}
                externalShowPreview={showWidgetPreview}
                onPreviewClose={() => setShowWidgetPreview(false)}
              />
            )}
          </div>
        );

      case 'license':
        return (
          <LicensePanel
            currentUser={currentUser}
            licenseKey={licenseKey}
            isLicenseActive={isLicenseActive}
            onActivateLicense={handleActivateLicense}
          />
        );

      case 'integrations':
        return (
          <IntegrationsPanel
            currentUser={currentUser}
            isLicenseActive={isLicenseActive}
          />
        );

      default:
        return (
          <DashboardPage
            userWidgets={userWidgets}
            isLicenseActive={isLicenseActive}
            onNavigate={handleNavigate}
          />
        );
    }
  };

  if (!isLoggedIn) {
    if (showRegister) {
      return (
        <div className="min-h-screen bg-blue-500 flex items-center justify-center p-4">
          <RegisterForm
            onRegister={handleRegister}
            onBackToLogin={() => setShowRegister(false)}
            users={mockUsers}
          />
        </div>
      );
    } else {
      return (
        <div className="min-h-screen bg-blue-500 flex items-center justify-center p-4">
          <LoginForm
            onLogin={handleLogin}
            onShowRegister={() => setShowRegister(true)}
            users={mockUsers}
          />
        </div>
      );
    }
  }

  return (
    <div className="App">
      <Header
        currentUser={currentUser}
        currentPage={currentPage}
        onLogout={handleLogout}
        onNavigate={handleNavigate}
        showAIAssistant={false}
        onShowPreview={() => setShowWidgetPreview(true)}
      />
     
      <Sidebar
        navigationItems={[
          { id: 'dashboard', label: 'Dashboard' },
          { id: 'widgetBuilder', label: 'Widget Builder' },
          { id: 'knowledgeBase', label: 'Knowledge Base' },
          { id: 'license', label: 'License' },
          { id: 'integrations', label: 'Integrations' }
        ]}
        currentPage={currentPage}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
        minimal={false}
      />
     
      <main className="dashboard-main">
        {renderMainContent()}
      </main>
    </div>
  );
}

export default App;